import React, { useState, useEffect } from 'react';
import './LobbyPage.css';

// LocalStorage keys
const STORAGE_KEYS = {
  CATEGORY: 'imposter_game_category',
  IMPOSTER_COUNT: 'imposter_game_imposter_count',
  HINTS_ENABLED: 'imposter_game_hints_enabled'
};

function LobbyPage({
  roomData,
  isHost,
  playerName,
  categories,
  socket,
  onSelectCategory,
  onSetImposterCount,
  onStartGame,
  onToggleHints,
  onSitOut,
  onJoinGame,
  onRemovePlayer,
  onDisconnect,
  error
}) {
  const [showAdultCategories, setShowAdultCategories] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  
  // Load saved hints setting from localStorage
  const loadSavedHints = () => {
    const saved = localStorage.getItem(STORAGE_KEYS.HINTS_ENABLED);
    return saved === 'true';
  };
  
  const [hintsEnabled, setHintsEnabled] = useState(loadSavedHints);
  
  // Load and restore saved settings when component mounts or room state changes to LOBBY
  useEffect(() => {
    if (isHost && roomData && roomData.state === 'LOBBY') {
      // Restore category
      const savedCategory = localStorage.getItem(STORAGE_KEYS.CATEGORY);
      if (savedCategory) {
        try {
          const category = JSON.parse(savedCategory);
          // Only restore if not already set or different
          if (!roomData.selectedCategory || roomData.selectedCategory.id !== category.id) {
            onSelectCategory(category.id);
          }
        } catch (e) {
          console.error('Error parsing saved category:', e);
        }
      }
      
      // Restore imposter count
      const savedImposterCount = localStorage.getItem(STORAGE_KEYS.IMPOSTER_COUNT);
      if (savedImposterCount) {
        const count = parseInt(savedImposterCount, 10);
        if (!roomData.imposterCount || roomData.imposterCount !== count) {
          onSetImposterCount(count);
        }
      }
      
      // Restore hints enabled
      const savedHints = loadSavedHints();
      if (savedHints !== hintsEnabled) {
        setHintsEnabled(savedHints);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, roomData?.state, roomData?.selectedCategory?.id, roomData?.imposterCount]);

  const activePlayers = roomData.players.filter(p => !p.sittingOut);
  // Default imposter count to 1 if not set
  const imposterCount = roomData.imposterCount || 1;
  const canStart = isHost && 
    roomData.selectedCategory && 
    imposterCount >= 1 &&
    activePlayers.length >= 3 &&
    roomData.state === 'LOBBY';

  // Debug: Log host controls visibility
  if (isHost) {
    console.log('Host controls should be visible. Room state:', roomData.state);
  }

  const filteredCategories = categories.filter(cat => 
    showAdultCategories || !cat.adult
  );


  return (
    <div className="lobby-page">
      <div className="lobby-container">
        <div className="lobby-header">
          <div>
            <h2>Room Code: <span className="room-code">{roomData.code}</span></h2>
            {isHost && <span className="host-badge">Host</span>}
          </div>
          <button 
            onClick={() => setShowLeaveConfirm(true)} 
            className="disconnect-btn"
          >
            Leave
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="player-section">
          <div className="player-section-header">
            <h3>Players</h3>
            <span className="player-count">{roomData.players?.length || 0}</span>
          </div>
          <div className="player-list">
            {roomData.players && roomData.players.length > 0 ? roomData.players.map(player => {
              const isCurrentPlayer = socket?.id === player.id;
              return (
                <div 
                  key={player.id} 
                  className={`player-item ${isCurrentPlayer ? 'current' : ''} ${player.sittingOut ? 'sitting-out' : ''}`}
                >
                  <div className="player-info">
                    <span className="player-name">{player.name}</span>
                    <div className="player-left-actions">
                      {isHost && !isCurrentPlayer && (
                        <>
                          <button 
                            onClick={() => player.sittingOut ? onJoinGame(player.id) : onSitOut(player.id)}
                            className="small-btn"
                          >
                            {player.sittingOut ? 'Add' : 'Sit Out'}
                          </button>
                          <button 
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to remove ${player.name} from the room?`)) {
                                onRemovePlayer(player.id);
                              }
                            }}
                            className="small-btn remove-btn"
                            title="Remove player"
                          >
                            Remove
                          </button>
                        </>
                      )}
                      {isCurrentPlayer && !player.sittingOut && (
                        <button 
                          onClick={() => onSitOut()}
                          className="small-btn"
                        >
                          Sit Out
                        </button>
                      )}
                      {isCurrentPlayer && player.sittingOut && (
                        <button 
                          onClick={() => onJoinGame()}
                          className="small-btn"
                        >
                          Join
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="player-badge-container">
                    {!player.sittingOut && <span className="player-badge">Active</span>}
                    {player.sittingOut && <span className="sitting-out-badge">Sitting Out</span>}
                  </div>
                </div>
              );
            }) : (
              <div className="no-players">No players yet...</div>
            )}
          </div>
        </div>

        {isHost && roomData.state === 'LOBBY' && (
          <div className="host-controls-section">
            <div className="control-group">
              <label>Category</label>
              <div className="adult-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={showAdultCategories}
                    onChange={(e) => setShowAdultCategories(e.target.checked)}
                  />
                  Show 18+ Categories
                </label>
              </div>
              <select
                value={roomData.selectedCategory?.id || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    onSelectCategory(e.target.value);
                  }
                }}
                className="select-field"
              >
                <option value="">Select Category</option>
                {filteredCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} {cat.adult && '🔞'}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label>Number of Imposters</label>
              <input
                type="number"
                min="1"
                max={activePlayers.length - 1}
                value={imposterCount}
                onChange={(e) => {
                  const count = parseInt(e.target.value) || 1;
                  onSetImposterCount(count);
                }}
                className="input-field"
                disabled={activePlayers.length < 2}
              />
              <small>Active players: {activePlayers.length}</small>
            </div>

            <div className="control-group">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                <input
                  type="checkbox"
                  checked={hintsEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setHintsEnabled(enabled);
                    onToggleHints(enabled);
                    // Save to localStorage immediately
                    localStorage.setItem(STORAGE_KEYS.HINTS_ENABLED, enabled.toString());
                  }}
                  style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold' }}>Enable Hints for Imposter</span>
              </label>
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                If enabled, the imposter will receive a hint about the word
              </small>
            </div>

            <button
              onClick={() => {
                // Save settings to localStorage before starting game
                if (roomData.selectedCategory) {
                  localStorage.setItem(STORAGE_KEYS.CATEGORY, JSON.stringify(roomData.selectedCategory));
                }
                if (roomData.imposterCount) {
                  localStorage.setItem(STORAGE_KEYS.IMPOSTER_COUNT, roomData.imposterCount.toString());
                }
                localStorage.setItem(STORAGE_KEYS.HINTS_ENABLED, hintsEnabled.toString());
                
                onStartGame(hintsEnabled);
              }}
              disabled={!canStart}
              className={`primary-btn ${!canStart ? 'disabled' : ''}`}
            >
              Start Game
            </button>
            {!canStart && isHost && (
              <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
                {!roomData.selectedCategory && 'Please select a category. '}
                {activePlayers.length < 3 && `Need ${3 - activePlayers.length} more player(s). `}
                {roomData.state !== 'LOBBY' && 'Game is already in progress.'}
              </div>
            )}
          </div>
        )}

        {!isHost && roomData.state === 'LOBBY' && (
          <div className="waiting-for-host">
            <div className="waiting-animation">
              <div className="waiting-dot"></div>
              <div className="waiting-dot"></div>
              <div className="waiting-dot"></div>
            </div>
            <p className="waiting-text">Waiting for host to start</p>
          </div>
        )}

        {roomData.state === 'GAME_ACTIVE' && (
          <div className="game-active-message">
            <p>Game in progress...</p>
            <button 
              onClick={() => {
                // Request to rejoin the active game
                if (socket) {
                  socket.emit('rejoin-active-game');
                }
              }}
              className="primary-btn"
              style={{ marginTop: '12px' }}
            >
              Continue Game
            </button>
          </div>
        )}

        {/* Leave Confirmation Modal */}
        {showLeaveConfirm && (
          <div className="modal-overlay" onClick={() => setShowLeaveConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Leave Room?</h3>
              <p>Are you sure you want to leave the room? You will need to rejoin with the room code.</p>
              <div className="modal-actions">
                <button onClick={() => setShowLeaveConfirm(false)} className="modal-btn cancel">
                  Cancel
                </button>
                <button onClick={() => {
                  setShowLeaveConfirm(false);
                  onDisconnect();
                }} className="modal-btn confirm">
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LobbyPage;

